import { useMemo } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import type { ResourcePresenceViewer } from '@/types/websocket'

interface PresenceAvatarStackProps {
	viewers: ResourcePresenceViewer[]
	maxVisible?: number
	className?: string
	/** Shown in tooltip header, e.g. "Viewing this workspace" */
	contextLabel?: string
}

function initialsForName(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length >= 2) {
		return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
	}
	const single = parts[0] ?? name
	return single.slice(0, 2).toUpperCase()
}

function colorClassForUser(userId: string): string {
	const palette = [
		'bg-violet-600',
		'bg-blue-600',
		'bg-emerald-600',
		'bg-amber-600',
		'bg-rose-600',
		'bg-cyan-600',
	]
	let hash = 0
	for (let index = 0; index < userId.length; index += 1) {
		hash = (hash + userId.charCodeAt(index)) % palette.length
	}
	return palette[hash] ?? palette[0]
}

export function PresenceAvatarStack({
	viewers,
	maxVisible = 4,
	className,
	contextLabel,
}: PresenceAvatarStackProps) {
	const { t } = useTranslation()

	const sortedViewers = useMemo(() => {
		const unique = new Map<string, ResourcePresenceViewer>()
		for (const viewer of viewers) {
			unique.set(viewer.user_id, viewer)
		}
		return Array.from(unique.values()).sort((left, right) =>
			left.display_name.localeCompare(right.display_name)
		)
	}, [viewers])

	if (sortedViewers.length === 0) {
		return null
	}

	const visible = sortedViewers.slice(0, maxVisible)
	const overflow = sortedViewers.length - visible.length
	const tooltipNames = sortedViewers.map((viewer) => viewer.display_name)

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					className={cn(
						'flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						className
					)}
					aria-label={t('common.presence.liveUsers', { count: sortedViewers.length })}
				>
					<div className="flex -space-x-2">
						{visible.map((viewer) => (
							<span
								key={viewer.user_id}
								className={cn(
									'inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-card text-[10px] font-semibold text-white',
									colorClassForUser(viewer.user_id)
								)}
								title={viewer.display_name}
							>
								{initialsForName(viewer.display_name)}
							</span>
						))}
						{overflow > 0 ? (
							<span className="inline-flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-muted text-[10px] font-medium text-muted-foreground">
								+{overflow}
							</span>
						) : null}
					</div>
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom" align="end" className="max-w-xs">
				<p className="mb-1 text-xs font-medium">{contextLabel ?? t('common.presence.viewingNow')}</p>
				<ul className="space-y-0.5 text-xs text-background/80">
					{tooltipNames.map((name) => (
						<li key={name}>{name}</li>
					))}
				</ul>
			</TooltipContent>
		</Tooltip>
	)
}
